import { lazy } from 'react';
import { useMatch, useParams } from 'react-router';
import { useOperatorPlatformId } from 'src/apis/monolith/use-operator-platform-id';
import type { SurrenderRequestDetailsResponse } from 'src/apis/surrender-service/types';
import {
  useSurrenderRequestDetails,
  type SurrenderRequestDetailsRequest,
} from 'src/apis/surrender-service/use-surrender-request-details';
import {
  useSurrenderRoot,
  type SurrenderRootRequest,
} from 'src/apis/surrender-service/use-surrender-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

export const SurrenderDetails = () => {
  const surrenderRoot = useSurrenderRoot();
  const { surrenderRequestId } = useParams<{ surrenderRequestId?: string }>();
  const surrenderRequestDetails = useSurrenderRequestDetails({
    surrenderRoot,
    surrenderRequestId,
  });
  const isTso = Boolean(useMatch('/reporting/shipper-transactions/*'));

  if (!surrenderRequestId) throw new Forbidden();
  if (!surrenderRoot.response || !surrenderRequestDetails?.response)
    return <PageSpinner />;

  const contractTsoId = isTso
    ? undefined
    : getContractTsoId(surrenderRequestDetails.response.data);

  if (contractTsoId)
    return (
      <OperatorPage
        tsoId={contractTsoId}
        surrenderRoot={surrenderRoot}
        surrenderRequestDetails={surrenderRequestDetails}
        isTso={isTso}
      />
    );

  return (
    <Page
      surrenderRoot={surrenderRoot}
      surrenderRequestDetails={surrenderRequestDetails}
      isTso={isTso}
      contractOperatorReferenceId={undefined}
    />
  );
};

type OperatorPageProps = {
  tsoId: string;
  surrenderRoot: Successful<SurrenderRootRequest>;
  surrenderRequestDetails: Successful<SurrenderRequestDetailsRequest>;
  isTso: boolean;
};

const OperatorPage = ({ tsoId, isTso, ...props }: OperatorPageProps) => {
  const operatorPlatformId = useOperatorPlatformId(tsoId);

  if (!operatorPlatformId.loaded) return <PageSpinner />;

  return (
    <Page
      {...props}
      contractOperatorReferenceId={
        operatorPlatformId.response?.data.referenceId
      }
      isTso={isTso}
    />
  );
};

const getContractTsoId = (data: SurrenderRequestDetailsResponse) => {
  if (data.process !== 'CAPACITY_MANAGEMENT' || data.bundled) return;
  const { contractId, contractName, contractType } = data.capacityManagement;
  if (!contractId || !contractName || !contractType) return;
  return data.unbundled.tsoId;
};
